import React, { useEffect, useState, useRef } from 'react';
import { BaseResourceListProps, SimpleList, SvgBin, SvgEdit, Form, Button } from '@app/components';
import { customerApi } from '@app/store';
import { ICustomer } from '@app/common';
import { theme } from '@app/theme';
import { createColumnHelper } from '@tanstack/react-table';
import { useTable } from '@app/hooks/useTable';
import { CheckPermissions } from '@app/components/CheckPermissions';
import { SearchInput } from '@app/components/form/SearchInput';
import { useForm } from 'react-hook-form';
import { useToast, useThrottle } from '@app/hooks';
import styled from '@2pulse/responsive';
import { row } from '@app/style';
import { normalize } from '@app/helpers/normalize';
import { ActivityIndicator, Modal, View, Text, Pressable } from 'react-native';

const columnHelper = createColumnHelper<ICustomer>();

const SearchContainer = styled.View({
  ...row,
  alignItems: 'center',
  gap: 16,
  paddingRight: 34,
});

const SearchWrapper = styled.View({
  flex: 1,
});

const ExportButton = styled.View({
  flexShrink: 0,
  ...row,
  alignItems: 'center',
  gap: 12,
});

const ExportOverlay = styled.View({
  flex: 1,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  justifyContent: 'center',
  alignItems: 'center',
});

const ExportModal = styled.View({
  backgroundColor: theme.colors.babypowder,
  borderRadius: normalize(16),
  padding: normalize(32),
  alignItems: 'center',
  minWidth: normalize(400),
  gap: normalize(20),
});

const ExportTitle = styled.Text({
  fontSize: normalize(24),
  fontWeight: 'bold',
  color: theme.colors.oxfordblue,
  textAlign: 'center',
});

const ExportProgress = styled.Text({
  fontSize: normalize(36),
  fontWeight: 'bold',
  color: theme.colors.celadonblue,
  textAlign: 'center',
});

const ExportMessage = styled.Text({
  fontSize: normalize(16),
  color: theme.colors.oxfordblue,
  textAlign: 'center',
  opacity: 0.7,
});

const CancelButton = styled.Pressable({
  marginTop: normalize(10),
  paddingHorizontal: normalize(24),
  paddingVertical: normalize(12),
  borderRadius: normalize(8),
  borderWidth: 2,
  borderColor: theme.colors.crimson,
});

const CancelButtonText = styled.Text({
  fontSize: normalize(16),
  fontWeight: 'bold',
  color: theme.colors.crimson,
});

const CSV_HEADERS = [
  'Nom',
  'Prénom',
  'Email',
  'Téléphone',
  'Date de naissance',
  'Age',
  'Opt-in Email',
  'Opt-in SMS',
  'Opt-in Fidélité',
  'Date inscription',
  'Points fidélité',
];

const BATCH_SIZE = 10000;
const PARALLEL_REQUESTS = 1;

export const CustomerList = ({ onUpdate, onDelete, onRowPress, selectedRowItem }: BaseResourceListProps<ICustomer>) => {
  const toast = useToast();
  const [exportCount] = customerApi.useExportCountMutation();
  const [exportCsv] = customerApi.useExportCsvMutation();
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });
  const cancelledRef = useRef(false);

  const searchFormMethods = useForm({
    defaultValues: {
      search: ''
    }
  });

  const searchValue = searchFormMethods.watch('search');
  const throttledSearch = useThrottle(searchValue, 350);

  const handleCancelExport = () => {
    cancelledRef.current = true;
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    setExportProgress({ current: 0, total: 0 });
    cancelledRef.current = false;
    
    try {
      // Premier appel pour récupérer le total (rapide)
      const { total } = await exportCount({}).unwrap();
      
      if (total === 0) {
        toast.error('Aucun client à exporter');
        return;
      }

      setExportProgress({ current: 0, total });

      // Calculer tous les batches
      const allBatches: { skip: number; index: number }[] = [];
      let batchIndex = 0;
      for (let skip = 0; skip < total; skip += BATCH_SIZE) {
        allBatches.push({ skip, index: batchIndex++ });
      }

      // Préparer les résultats
      const allResults: { index: number; rows: string[][] }[] = [];
      let fetchedCount = 0;

      // Exécuter les batches en parallèle par groupes
      for (let i = 0; i < allBatches.length; i += PARALLEL_REQUESTS) {
        // Vérifier si annulé
        if (cancelledRef.current) {
          toast.open('Export annulé');
          return;
        }

        const batchGroup = allBatches.slice(i, i + PARALLEL_REQUESTS);
        
        const results = await Promise.all(
          batchGroup.map(async ({ skip, index }) => {
            const result = await exportCsv({ skip, take: BATCH_SIZE }).unwrap();
            return { index, rows: result.rows };
          })
        );

        allResults.push(...results);
        fetchedCount += results.reduce((sum, r) => sum + r.rows.length, 0);
        setExportProgress({ current: fetchedCount, total });
      }

      // Vérifier si annulé avant de générer le fichier
      if (cancelledRef.current) {
        toast.open('Export annulé');
        return;
      }

      // Trier par index et combiner les lignes
      allResults.sort((a, b) => a.index - b.index);
      const allRows = allResults.flatMap(r => r.rows);

      // Générer le CSV
      const csvContent = [
        CSV_HEADERS.join(';'),
        ...allRows.map(row => row.join(';'))
      ].join('\n');

      // Encoder en UTF-8 avec BOM
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toLocaleDateString('fr-FR').replace(/\//g, '-');
      a.download = `Clients_${today}.csv`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 0);

      toast.success(`${allRows.length} client(s) exporté(s) avec succès`);
    } catch (error) {
      if (!cancelledRef.current) {
        console.error('Erreur lors de l\'export CSV:', error);
        toast.error('Une erreur est survenue lors de l\'export des clients');
      }
    } finally {
      setIsExporting(false);
      setExportProgress({ current: 0, total: 0 });
    }
  };
  const columns = [
    columnHelper.accessor('firstname', {
      header: () => (
        <SimpleList.HeaderItem width="25%">
          <SimpleList.HeaderItemLabel>Prénom</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => (
        <SimpleList.ItemCol width="25%">
          <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
            {info.getValue()}
          </SimpleList.ItemText>
        </SimpleList.ItemCol>
      ),
    }),
    columnHelper.accessor('lastname', {
      header: () => (
        <SimpleList.HeaderItem width="25%">
          <SimpleList.HeaderItemLabel>Nom</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => (
        <SimpleList.ItemCol width="25%">
          <SimpleList.ItemText align="left" bold numberOfLines={2} active={info.active}>
            {info.getValue()}
          </SimpleList.ItemText>
        </SimpleList.ItemCol>
      ),
    }),
    columnHelper.accessor('email', {
      header: () => (
        <SimpleList.HeaderItem width="25%">
          <SimpleList.HeaderItemLabel>Email</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: (info) => (
        <SimpleList.ItemCol width="25%">
          <SimpleList.ItemText align="left" numberOfLines={2} active={info.active}>
            {info.getValue()}
          </SimpleList.ItemText>
        </SimpleList.ItemCol>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <SimpleList.HeaderItem width="25%" align='center'>
          <SimpleList.HeaderItemLabel>Actions</SimpleList.HeaderItemLabel>
        </SimpleList.HeaderItem>
      ),
      cell: ({ row, active }) => (
        <SimpleList.ItemCol width="25%">
          <SimpleList.ItemActions>
            <CheckPermissions permissions={['dashboard::customers.update']}>
              <SimpleList.ItemAction onPress={() => onUpdate(row.original)}>
                <SvgEdit color={active ? '#FFF' : theme.colors.celadonblue} />
              </SimpleList.ItemAction>
            </CheckPermissions>

            <CheckPermissions permissions={['dashboard::customers.delete']}>
              <SimpleList.ItemAction onPress={() => onDelete(row.original)}>
                <SvgBin color={active ? '#FFF' : theme.colors.celadonblue} />
              </SimpleList.ItemAction>
            </CheckPermissions>
          </SimpleList.ItemActions>
        </SimpleList.ItemCol >
      ),
    }),
  ];

  const { table, isFetching } = useTable({
    columns,
    useQuery: ({ pagination }) => customerApi.useReadManyCustomersQuery({
      pagination,
      filters: {
        $or: [
          { firstname: { $contains: throttledSearch } },
          { lastname: { $contains: throttledSearch } },
          { email: { $contains: throttledSearch } },
          { phone: { $contains: throttledSearch } }
        ]
      },
      sort: ['lastname:ASC', 'firstname:ASC']
    })
  });

  // Reset pagination when search changes
  useEffect(() => {
    table.setPageIndex(0);
  }, [throttledSearch, table]);

  const progressPercent = exportProgress.total > 0 
    ? Math.round((exportProgress.current / exportProgress.total) * 100) 
    : 0;

  return (
    <>
      <Modal
        visible={isExporting}
        transparent
        animationType="fade"
      >
        <ExportOverlay>
          <ExportModal>
            <ActivityIndicator size="large" color={theme.colors.celadonblue} />
            <ExportTitle>Export en cours...</ExportTitle>
            <ExportProgress>
              {exportProgress.current.toLocaleString('fr-FR')} / {exportProgress.total.toLocaleString('fr-FR')}
            </ExportProgress>
            <ExportMessage>
              {progressPercent}% - Veuillez ne pas quitter cette page
            </ExportMessage>
            <CancelButton onPress={handleCancelExport}>
              <CancelButtonText>Annuler l'export</CancelButtonText>
            </CancelButton>
          </ExportModal>
        </ExportOverlay>
      </Modal>

      <SimpleList
        table={table}
        isFetching={isFetching}
        selectedRowItem={selectedRowItem}
        onRowPress={onRowPress}
        SearchComponent={(
          <Form formMethods={searchFormMethods}>
            <SearchContainer>
              <SearchWrapper>
                <SearchInput name='search' label='' placeholder='Recherche..' />
              </SearchWrapper>
              <ExportButton>
                <CheckPermissions permissions={['dashboard::customers.read']}>
                  <Button
                    onPress={handleExportCSV}
                    title='Exporter'
                    disabled={isExporting}
                    inverted
                    extraStyle={{ paddingHorizontal: 20, paddingVertical: 10 }} />
                </CheckPermissions>
              </ExportButton>
            </SearchContainer>
          </Form>
        )} />
    </>
  );
}; 